import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import SunOutlined from '@ant-design/icons/SunOutlined';
import MoonOutlined from '@ant-design/icons/MoonOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CameraOutlined from '@ant-design/icons/CameraOutlined';
import FolderOpenOutlined from '@ant-design/icons/FolderOpenOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

import IconButton from 'components/@extended/IconButton';
import DrawerHeader from 'components/ui/DrawerHeader';
import avatar1 from 'assets/images/users/avatar-1.png';
import { HEADER_HEIGHT } from '../index';
import { clearMenuAccessCache } from 'api/access';
import { fetchEmployee, saveEmployeeInfoSection } from 'api/employees';
import { API_BASE_URL } from 'api/client';
import Notification from './HeaderContent/Notification';
import HelpSupport from './HeaderContent/HelpSupport';
import { metallicPalette } from 'themes/colors';
import {
  COLOR_PRESETS,
  applyGlobalThemeMode,
  getStoredThemeMode,
  getStoredPresetColor
} from 'themes/themeMode';

// ── Profile Right-Side Drawer Panel with Theme Customizer ─────────────────────

const PROFILE_PHOTO_STORAGE_KEY = 'userProfilePhoto';

function firstFilled(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '') || '';
}

function getProfilePhotoFromEmployee(employee = {}) {
  return firstFilled(
    employee.profilePhoto,
    employee.profile_photo,
    employee.profileImage,
    employee.profileImageUrl,
    employee.avatarUrl,
    employee.profilePhotoFilePath,
    employee.photo,
    employee.image
  );
}

function getStoredProfilePhoto() {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(PROFILE_PHOTO_STORAGE_KEY) || '';
}

function setStoredProfilePhoto(value) {
  if (typeof window === 'undefined') return;
  if (value) localStorage.setItem(PROFILE_PHOTO_STORAGE_KEY, value);
  else localStorage.removeItem(PROFILE_PHOTO_STORAGE_KEY);
}

function getProfileImageUrl(value) {
  if (!value) return '';
  const path = String(value);
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:') || path.startsWith('data:')) return path;
  const apiOrigin = API_BASE_URL.replace(/\/api$/, '');
  return `${apiOrigin}${path.startsWith('/') ? path : `/${path}`}`;
}

function ProfileAvatar({ profilePhoto, userName, size = 38, active = false, sx = {} }) {
  const [imageError, setImageError] = useState(false);
  const imageUrl = imageError ? '' : getProfileImageUrl(profilePhoto);

  useEffect(() => {
    setImageError(false);
  }, [profilePhoto]);

  return (
    <Avatar
      src={imageUrl || avatar1}
      alt={userName || 'User'}
      imgProps={{ onError: () => setImageError(true) }}
      sx={{
        width: size,
        height: size,
        bgcolor: alpha(metallicPalette.primary.main, 0.1),
        color: metallicPalette.primary.main,
        border: `2px solid ${active ? metallicPalette.primary.main : alpha(metallicPalette.primary.main, 0.28)}`,
        boxShadow: `0 2px 8px ${alpha(metallicPalette.primary.main, 0.15)}`,
        flexShrink: 0,
        transition: 'all 0.2s ease',
        '& .MuiAvatar-img': {
          objectFit: 'cover'
        },
        ...sx
      }}
    />
  );
}

function CameraCaptureDialog({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => {
    let currentStream = null;
    if (open) {
      setCapturedPhoto(null);
      setCameraError('');
      if (!navigator?.mediaDevices?.getUserMedia) {
        setCameraError('Camera access is not supported on this device/browser.');
        return;
      }
      navigator.mediaDevices
        .getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 640 }, facingMode: 'user' },
          audio: false
        })
        .then((s) => {
          currentStream = s;
          setStream(s);
          if (videoRef.current) {
            videoRef.current.srcObject = s;
            videoRef.current.play().catch(() => {});
          }
        })
        .catch((err) => {
          console.error('Camera access error:', err);
          setCameraError('Unable to open camera. Please ensure camera permissions are allowed in your browser.');
        });
    }

    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [open]);

  const handleStopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    onClose();
  };

  const handleTakePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const size = Math.min(video.videoWidth || 640, video.videoHeight || 640);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const startX = ((video.videoWidth || size) - size) / 2;
    const startY = ((video.videoHeight || size) - size) / 2;
    ctx.drawImage(video, startX, startY, size, size, 0, 0, size, size);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setCapturedPhoto(dataUrl);

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const handleRetake = () => {
    setCapturedPhoto(null);
    setCameraError('');
    navigator.mediaDevices
      .getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 640 }, facingMode: 'user' },
        audio: false
      })
      .then((s) => {
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch(() => {
        setCameraError('Unable to restart camera.');
      });
  };

  const handleUsePhoto = () => {
    if (capturedPhoto) {
      onCapture(capturedPhoto);
      handleStopCamera();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleStopCamera}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: '16px', overflow: 'hidden' }
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>
          Take Profile Photo
        </Typography>
        <IconButton size="small" onClick={handleStopCamera} sx={{ color: '#9CA3AF' }}>
          <CloseOutlined style={{ fontSize: 13 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#F9FAFB' }}>
        {cameraError ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center', color: '#DC2626' }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{cameraError}</Typography>
          </Box>
        ) : capturedPhoto ? (
          <Box sx={{ width: 260, height: 260, borderRadius: '50%', overflow: 'hidden', border: '3px solid #3B82F6', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', mb: 2 }}>
            <img src={capturedPhoto} alt="Captured" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        ) : (
          <Box sx={{ width: 260, height: 260, borderRadius: '50%', overflow: 'hidden', bgcolor: '#000000', border: '3px solid #E5E7EB', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', mb: 2, position: 'relative' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
            />
          </Box>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {!cameraError && (
          <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ width: '100%', mt: 1 }}>
            {capturedPhoto ? (
              <>
                <ButtonBase
                  onClick={handleRetake}
                  sx={{
                    py: 1,
                    px: 2.5,
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    bgcolor: '#FFFFFF',
                    color: '#374151',
                    fontSize: 13,
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#F3F4F6' }
                  }}
                >
                  Retake
                </ButtonBase>
                <ButtonBase
                  onClick={handleUsePhoto}
                  sx={{
                    py: 1,
                    px: 2.5,
                    borderRadius: '8px',
                    bgcolor: '#2563EB',
                    color: '#FFFFFF',
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow: '0 2px 6px rgba(37,99,235,0.3)',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#1D4ED8' }
                  }}
                >
                  Save Photo
                </ButtonBase>
              </>
            ) : (
              <ButtonBase
                onClick={handleTakePhoto}
                sx={{
                  py: 1.2,
                  px: 3,
                  borderRadius: '30px',
                  bgcolor: '#2563EB',
                  color: '#FFFFFF',
                  fontSize: 13.5,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  boxShadow: '0 4px 12px rgba(37,99,235,0.35)',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#1D4ED8', transform: 'scale(1.02)' }
                }}
              >
                <CameraOutlined style={{ fontSize: 16 }} /> Capture Photo
              </ButtonBase>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProfileDrawer() {
  const [open, setOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(getStoredThemeMode);
  const [presetColor, setPresetColor] = useState(getStoredPresetColor);
  const [profilePhoto, setProfilePhoto] = useState(getStoredProfilePhoto);
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState(null);
  const [viewPhotoOpen, setViewPhotoOpen] = useState(false);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const userName = localStorage.getItem('userName') || 'HRMS Admin';
  const userRole = localStorage.getItem('userRole') || 'HR Admin';
  const userEmail = localStorage.getItem('userEmail') || 'admin@workora.com';

  useEffect(() => {
    applyGlobalThemeMode(themeMode, presetColor);
  }, [themeMode, presetColor]);

  useEffect(() => {
    let active = true;

    const syncProfilePhoto = (nextPhoto) => {
      if (!active) return;
      const value = nextPhoto || '';
      setProfilePhoto(value);
      setStoredProfilePhoto(value);
    };

    const loadProfilePhoto = () => {
      const employeeId = localStorage.getItem('employeeId');
      const storedPhoto = getStoredProfilePhoto();
      if (storedPhoto) setProfilePhoto(storedPhoto);

      if (!employeeId) {
        syncProfilePhoto('');
        return;
      }

      fetchEmployee(employeeId, { silentError: true })
        .then((data) => syncProfilePhoto(getProfilePhotoFromEmployee(data.employee)))
        .catch(() => {
          if (!storedPhoto) syncProfilePhoto('');
        });
    };

    const handleProfileUpdated = (event) => {
      if (event?.detail && Object.prototype.hasOwnProperty.call(event.detail, 'profilePhoto')) {
        syncProfilePhoto(event.detail.profilePhoto);
        return;
      }
      loadProfilePhoto();
    };

    const handleStorage = (event) => {
      if (event.key === PROFILE_PHOTO_STORAGE_KEY) setProfilePhoto(event.newValue || '');
    };

    loadProfilePhoto();
    window.addEventListener('hrmsProfileUpdated', handleProfileUpdated);
    window.addEventListener('hrmsPermissionsUpdated', loadProfilePhoto);
    window.addEventListener('storage', handleStorage);

    return () => {
      active = false;
      window.removeEventListener('hrmsProfileUpdated', handleProfileUpdated);
      window.removeEventListener('hrmsPermissionsUpdated', loadProfilePhoto);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  function handleModeChange(mode) {
    setThemeMode(mode);
    applyGlobalThemeMode(mode, presetColor);
  }

  function handleColorChange(colorId) {
    setPresetColor(colorId);
    applyGlobalThemeMode(themeMode, colorId);
  }

  function handleLogout() {
    clearMenuAccessCache();
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('employeeId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem(PROFILE_PHOTO_STORAGE_KEY);
    window.dispatchEvent(new Event('hrmsPermissionsUpdated'));
    navigate('/login');
  }

  function handleMyProfile() {
    const employeeId = localStorage.getItem('employeeId');
    setOpen(false);
    navigate(employeeId ? `/employees/add?view=${encodeURIComponent(employeeId)}` : '/employees/employee');
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoMenuAnchor(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      setProfilePhoto(base64);
      setStoredProfilePhoto(base64);
      window.dispatchEvent(new CustomEvent('hrmsProfileUpdated', { detail: { profilePhoto: base64 } }));
    };
    reader.readAsDataURL(file);

    const employeeId = localStorage.getItem('employeeId');
    if (employeeId) {
      try {
        const response = await saveEmployeeInfoSection(employeeId, { profilePhoto: file });
        const savedPhoto = response?.data?.profilePhoto || response?.data?.profile_photo || response?.data?.profilePhotoFilePath;
        if (savedPhoto) {
          setProfilePhoto(savedPhoto);
          setStoredProfilePhoto(savedPhoto);
          window.dispatchEvent(new CustomEvent('hrmsProfileUpdated', { detail: { profilePhoto: savedPhoto } }));
        }
      } catch (err) {
        console.error('Failed to sync profile photo with backend:', err);
      }
    }
    e.target.value = '';
  };

  const handleSaveCapturedPhoto = async (dataUrl) => {
    setProfilePhoto(dataUrl);
    setStoredProfilePhoto(dataUrl);
    window.dispatchEvent(new CustomEvent('hrmsProfileUpdated', { detail: { profilePhoto: dataUrl } }));

    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `profile-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      const employeeId = localStorage.getItem('employeeId');
      if (employeeId) {
        const response = await saveEmployeeInfoSection(employeeId, { profilePhoto: file });
        const savedPhoto = response?.data?.profilePhoto || response?.data?.profile_photo || response?.data?.profilePhotoFilePath;
        if (savedPhoto) {
          setProfilePhoto(savedPhoto);
          setStoredProfilePhoto(savedPhoto);
          window.dispatchEvent(new CustomEvent('hrmsProfileUpdated', { detail: { profilePhoto: savedPhoto } }));
        }
      }
    } catch (err) {
      console.error('Failed to sync captured photo to backend:', err);
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoMenuAnchor(null);
    setProfilePhoto('');
    setStoredProfilePhoto('');
    window.dispatchEvent(new CustomEvent('hrmsProfileUpdated', { detail: { profilePhoto: '' } }));

    const employeeId = localStorage.getItem('employeeId');
    if (employeeId) {
      try {
        await saveEmployeeInfoSection(employeeId, { profilePhoto: null });
      } catch (err) {
        console.error('Failed to remove profile photo from backend:', err);
      }
    }
  };

  const menuItems = [
    { icon: UserOutlined, label: 'My Profile', desc: 'View and manage your employee profile', action: handleMyProfile }
  ];

  return (
    <Box>
      <Tooltip title="My Profile & Account">
        <ButtonBase
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: '50%',
            p: 0.25,
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: `0 0 0 3px ${alpha(metallicPalette.primary.main, 0.18)}`
            }
          }}
        >
          <ProfileAvatar profilePhoto={profilePhoto} userName={userName} size={28} active={open} />
        </ButtonBase>
      </Tooltip>

      {/* ── Slide-Out Profile Right Panel ── */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 5,
          '& .MuiBackdrop-root': {
            top: `${HEADER_HEIGHT}px`,
            bgcolor: 'rgba(15, 23, 42, 0.58)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          },
          '& .MuiDrawer-paper': {
            top: `${HEADER_HEIGHT}px !important`,
            height: `calc(100vh - ${HEADER_HEIGHT}px) !important`,
            maxHeight: `calc(100vh - ${HEADER_HEIGHT}px) !important`,
            width: { xs: '100vw', sm: 340 },
            bgcolor: themeMode === 'dark' ? '#1E293B' : '#FFFFFF',
            borderLeft: themeMode === 'dark' ? '1px solid #334155' : 'none',
            boxShadow: '-4px 10px 30px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            m: '0 !important',
            p: '0 !important'
          }
        }}
      >
        {/* Slim Reusable Global Drawer Header (Single Line 42px) with Sign Out Action Button */}
        <DrawerHeader
          title={`${userRole} Profile`}
          onClose={() => setOpen(false)}
          actions={
            <Tooltip title="Sign Out">
              <IconButton
                size="small"
                onClick={handleLogout}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: '#FCE8E6',
                  color: '#C5221F',
                  border: '1px solid #FAD2CF',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    bgcolor: '#C5221F',
                    color: '#FFFFFF',
                    borderColor: '#C5221F',
                    transform: 'scale(1.08)'
                  }
                }}
              >
                <LogoutOutlined style={{ fontSize: 11, fontWeight: 'bold' }} />
              </IconButton>
            </Tooltip>
          }
        />

        {/* ── Scrollable Body ── */}
        <Box className="slim-scroll" sx={{ flex: 1, px: 2, pt: 1, pb: 2, overflowY: 'auto' }}>
          {/* Centered Profile Summary */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              pt: 0.5,
              pb: 1,
              mb: 1
            }}
          >
            <Box
              sx={{ position: 'relative', display: 'inline-block', mb: 0.8, cursor: 'pointer' }}
              onClick={(e) => setPhotoMenuAnchor(e.currentTarget)}
            >
              <ProfileAvatar
                profilePhoto={profilePhoto}
                userName={userName}
                size={82}
                active
                sx={{
                  border: `2px solid ${alpha(metallicPalette.primary.main, 0.3)}`,
                  boxShadow: `0 4px 14px ${alpha(metallicPalette.primary.darker, 0.12)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': { opacity: 0.92 }
                }}
              />
              <Tooltip title="Photo Options">
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: '#FFFFFF',
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1.5px solid #E5E7EB',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#F3F4F6', transform: 'scale(1.1)' }
                  }}
                >
                  <CameraOutlined style={{ fontSize: 13 }} />
                </Box>
              </Tooltip>
            </Box>

            <Typography noWrap sx={{ maxWidth: '100%', fontSize: 18, fontWeight: 700, color: metallicPalette.grey[9], lineHeight: 1.2, mb: 0.3 }}>
              {userName}
            </Typography>
            <Typography noWrap sx={{ maxWidth: '100%', fontSize: 12, fontWeight: 500, color: metallicPalette.grey[6], lineHeight: 1.2, mb: 0.8 }}>
              {userEmail}
            </Typography>
            <Box
              sx={{
                px: 1.4,
                py: 0.25,
                bgcolor: alpha(metallicPalette.primary.main, 0.1),
                color: metallicPalette.primary.main,
                borderRadius: '999px',
                fontSize: 10.5,
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              {userRole}
            </Box>
          </Box>

          {/* ── Photo Actions Dropdown Menu (Image 2) ── */}
          <Menu
            anchorEl={photoMenuAnchor}
            open={Boolean(photoMenuAnchor)}
            onClose={() => setPhotoMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 170,
                  p: 0.5,
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #E5E7EB'
                }
              }
            }}
          >
            <MenuItem
              onClick={() => { setPhotoMenuAnchor(null); setViewPhotoOpen(true); }}
              sx={{ py: 0.8, px: 1.2, borderRadius: '8px', fontSize: 13, fontWeight: 500, color: '#1F2937', gap: 1.2 }}
            >
              <EyeOutlined style={{ fontSize: 15, color: '#374151' }} />
              View photo
            </MenuItem>
            <MenuItem
              onClick={() => { setPhotoMenuAnchor(null); setCameraDialogOpen(true); }}
              sx={{ py: 0.8, px: 1.2, borderRadius: '8px', fontSize: 13, fontWeight: 500, color: '#1F2937', gap: 1.2 }}
            >
              <CameraOutlined style={{ fontSize: 15, color: '#374151' }} />
              Take photo
            </MenuItem>
            <MenuItem
              onClick={() => { setPhotoMenuAnchor(null); fileInputRef.current?.click(); }}
              sx={{ py: 0.8, px: 1.2, borderRadius: '8px', fontSize: 13, fontWeight: 500, color: '#1F2937', gap: 1.2 }}
            >
              <FolderOpenOutlined style={{ fontSize: 15, color: '#374151' }} />
              Upload photo
            </MenuItem>
            <Divider sx={{ my: 0.5, borderColor: '#F3F4F6' }} />
            <MenuItem
              onClick={handleRemovePhoto}
              sx={{ py: 0.8, px: 1.2, borderRadius: '8px', fontSize: 13, fontWeight: 500, color: '#DC2626', gap: 1.2 }}
            >
              <DeleteOutlined style={{ fontSize: 15, color: '#DC2626' }} />
              Remove photo
            </MenuItem>
          </Menu>

          {/* Hidden input for File Upload */}
          <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />

          {/* ── Live Camera Capture Modal ── */}
          <CameraCaptureDialog
            open={cameraDialogOpen}
            onClose={() => setCameraDialogOpen(false)}
            onCapture={handleSaveCapturedPhoto}
          />

          {/* ── View Photo Dialog ── */}
          <Dialog
            open={viewPhotoOpen}
            onClose={() => setViewPhotoOpen(false)}
            maxWidth="xs"
            fullWidth
            slotProps={{
              paper: {
                sx: { borderRadius: '16px', overflow: 'hidden' }
              }
            }}
          >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>
                Profile Photo
              </Typography>
              <IconButton size="small" onClick={() => setViewPhotoOpen(false)} sx={{ color: '#9CA3AF' }}>
                <CloseOutlined style={{ fontSize: 13 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#F9FAFB' }}>
              <ProfileAvatar
                profilePhoto={profilePhoto}
                userName={userName}
                size={220}
                sx={{
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  border: '3px solid #FFFFFF'
                }}
              />
            </DialogContent>
          </Dialog>

          {/* ── Theme Mode Options (Light / Dark) ── */}
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: metallicPalette.grey[5],
              textTransform: 'uppercase',
              px: 0.5,
              mb: 0.8,
              letterSpacing: 0.5
            }}
          >
            Theme Mode
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {/* Light Mode Button */}
            <ButtonBase
              onClick={() => handleModeChange('light')}
              sx={{
                flex: 1,
                py: 1,
                px: 1.5,
                borderRadius: '8px',
                bgcolor: themeMode === 'light' ? alpha(metallicPalette.primary.main, 0.12) : metallicPalette.grey[1],
                border: `1.5px solid ${themeMode === 'light' ? metallicPalette.primary.main : metallicPalette.grey[3]}`,
                color: themeMode === 'light' ? metallicPalette.primary.main : metallicPalette.grey[7],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                fontSize: 12,
                fontWeight: 700,
                transition: 'all 0.2s ease'
              }}
            >
              <SunOutlined style={{ fontSize: 14 }} /> Light
            </ButtonBase>

            {/* Dark Mode Button */}
            <ButtonBase
              onClick={() => handleModeChange('dark')}
              sx={{
                flex: 1,
                py: 1,
                px: 1.5,
                borderRadius: '8px',
                bgcolor: themeMode === 'dark' ? alpha(metallicPalette.primary.main, 0.15) : metallicPalette.grey[1],
                border: `1.5px solid ${themeMode === 'dark' ? metallicPalette.primary.main : metallicPalette.grey[3]}`,
                color: themeMode === 'dark' ? metallicPalette.primary.main : metallicPalette.grey[7],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                fontSize: 12,
                fontWeight: 700,
                transition: 'all 0.2s ease'
              }}
            >
              <MoonOutlined style={{ fontSize: 14 }} /> Dark
            </ButtonBase>
          </Stack>

          {/* ── Brand Color Scheme Swatches ── */}
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: metallicPalette.grey[5],
              textTransform: 'uppercase',
              px: 0.5,
              mb: 1,
              letterSpacing: 0.5
            }}
          >
            Color Theme
          </Typography>

          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.5, px: 0.5 }}>
            {COLOR_PRESETS.map((preset) => {
              const isSelected = presetColor === preset.id;
              return (
                <Tooltip key={preset.id} title={preset.name}>
                  <ButtonBase
                    onClick={() => handleColorChange(preset.id)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: preset.main,
                      border: `2px solid ${isSelected ? '#FFFFFF' : 'transparent'}`,
                      boxShadow: isSelected ? `0 0 0 2.5px ${preset.main}` : '0 2px 5px rgba(0,0,0,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.15)'
                      }
                    }}
                  >
                    {isSelected && <CheckOutlined style={{ fontSize: 12, fontWeight: 'bold' }} />}
                  </ButtonBase>
                </Tooltip>
              );
            })}
          </Stack>

          {/* ── Navigation Menu (Account Quick Access) ── */}
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: metallicPalette.grey[5],
              textTransform: 'uppercase',
              px: 0.5,
              mb: 1,
              letterSpacing: 0.5
            }}
          >
            Account Quick Access
          </Typography>

          <List disablePadding>
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.label}
                  onClick={() => { setOpen(false); item.action(); }}
                  sx={{
                    py: 1.2,
                    px: 1.5,
                    borderRadius: '10px',
                    mb: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(metallicPalette.primary.main, 0.08)
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: alpha(metallicPalette.primary.main, 0.12),
                      color: metallicPalette.primary.main,
                      border: `1px solid ${alpha(metallicPalette.primary.main, 0.22)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                    }}
                  >
                    <Icon style={{ fontSize: 16, display: 'block', margin: 'auto' }} />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: metallicPalette.grey[9], lineHeight: 1.2 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: metallicPalette.grey[5], lineHeight: 1.3, mt: 0.3 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}

// ── Main Common Header Component ─────────────────────────────────────────────

export default function Header() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const handleThemeChange = () => setTick((t) => t + 1);
    window.addEventListener('hrmsThemeChanged', handleThemeChange);
    return () => window.removeEventListener('hrmsThemeChanged', handleThemeChange);
  }, []);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: 0,
        left: 0,
        right: 0,
        height: HEADER_HEIGHT,
        zIndex: (theme) => theme.zIndex.drawer + 10,
        background: `linear-gradient(135deg, ${metallicPalette.grey[0]} 0%, ${metallicPalette.primary.lighter} 30%, ${alpha(metallicPalette.primary.main, 0.18)} 75%, ${alpha(metallicPalette.primary.main, 0.28)} 100%)`,
        backdropFilter: 'blur(12px)',
        borderBottom: `1.5px solid ${alpha(metallicPalette.primary.main, 0.2)}`,
        color: metallicPalette.primary.darker,
        boxShadow: `0 4px 20px ${alpha(metallicPalette.primary.darker, 0.06)}`,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent 0%, ${alpha('#ffffff', 0.8)} 25%, ${alpha(metallicPalette.primary.light || metallicPalette.primary.main, 0.6)} 60%, transparent 100%)`,
          pointerEvents: 'none',
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -20,
          right: '15%',
          width: 350,
          height: 120,
          background: `radial-gradient(ellipse at center, ${alpha(metallicPalette.primary.main, 0.2)} 0%, ${alpha(metallicPalette.primary.light || metallicPalette.primary.main, 0.1)} 45%, transparent 70%)`,
          filter: 'blur(15px)',
          pointerEvents: 'none',
          zIndex: 0
        }
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          height: HEADER_HEIGHT,
          minHeight: `${HEADER_HEIGHT}px !important`,
          px: 2.5,
          gap: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Clean Typography Text Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            className="workora-logo"
            sx={{
              fontSize: 18,
              fontWeight: 800,
              color: '#0f172a',
              letterSpacing: -0.5,
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            Workora
          </Typography>
        </Box>

        {/* Flexible Spacer to push actions to right edge */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right Header Actions */}
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpSupport />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, height: 18, alignSelf: 'center', borderColor: alpha(metallicPalette.primary.main, 0.15) }}
          />

          <Notification />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, height: 18, alignSelf: 'center', borderColor: alpha(metallicPalette.primary.main, 0.15) }}
          />

          <ProfileDrawer />
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
